import env from '../../env';
import axios from 'axios';
import { createAsyncThunk } from '@reduxjs/toolkit';

function serializeAxiosError(err) {
  const status = err.response?.status;
  const data = err.response?.data;
  let message = err.message || 'Netzwerkfehler';
  if (typeof data === 'string') {
    message = data;
  } else if (data && typeof data === 'object') {
    // API wraps Strapi errors as: { provider, status, data: { data: null, error: { message } } }
    const nested = data.data;
    const nestedMsg = nested && typeof nested === 'object' ? nested.error?.message || nested.message : null;
    message = nestedMsg || data.message || (typeof data.error === 'string' ? data.error : null) || message;
  }
  return { message, status, data, requestId: data?.requestId };
}

export const ssoLogin = createAsyncThunk('account/ssoLogin', async (arg, { rejectWithValue }) => {
  try {
    const { data } = await axios.get(`${env.REACT_APP_SSO_BACKEND}/auth/me`, {
      headers: {
        jwt: localStorage.getItem('microsoftJWT'),
      },
    });
    return data;
  } catch (err) {
    return rejectWithValue(serializeAxiosError(err));
  }
});

export const localLogin = createAsyncThunk('account/localLogin', async (arg, { rejectWithValue }) => {
  try {
    const { data } = await axios.post(`${env.REACT_APP_SSO_BACKEND}/auth/local/login`, arg);
    return data;
  } catch (err) {
    return rejectWithValue(serializeAxiosError(err));
  }
});

export const getDataFromSessionID = createAsyncThunk(
  'account/getDataFromSessionID',
  async (searchParams, { rejectWithValue }) => {
    const qs =
      searchParams instanceof URLSearchParams ? searchParams.toString() : String(searchParams || '');
    const sp = new URLSearchParams(
      qs.startsWith('?') ? qs.slice(1) : qs
    );
    let sessionId = sp.get('sessionId') || sp.get('sessionid');
    if (!sessionId) {
      for (const [k, v] of sp.entries()) {
        if (k.toLowerCase() === 'sessionid') {
          sessionId = v;
          break;
        }
      }
    }
    if (!sessionId) {
      const hasCode = Boolean(sp.get('code'));
      return rejectWithValue({
        message: hasCode
          ? 'URL enthält ?code=… (Microsoft) aber keine sessionId. In Entra die Redirect-URI auf die API setzen: {API}/auth/sso/callback — oder Seite neu laden, nachdem die API umgestellt ist.'
          : 'Keine sessionId in der URL — nach dem Login muss die API auf /auth/callback?sessionId=… weiterleiten (REDIRECT_SUCCESS_LOGIN).',
        code: 'missing_session_id',
        hasCode,
      });
    }
    const qsNormalized = `sessionId=${encodeURIComponent(sessionId)}`;
    try {
      const { data, status } = await axios.get(
        `${env.REACT_APP_SSO_BACKEND}/auth/sso/callback_get_token?${qsNormalized}`,
        { validateStatus: () => true }
      );
      if (status >= 400) {
        return rejectWithValue({
          message:
            (typeof data === 'object' && data?.message) ||
            (typeof data === 'object' && data?.error) ||
            `Session-Token konnte nicht geladen werden (HTTP ${status}).`,
          status,
          data,
          requestId: typeof data === 'object' ? data?.requestId : undefined,
        });
      }
      if (typeof data !== 'object' || data === null || Array.isArray(data)) {
        return rejectWithValue({
          message:
            'Ungültige API-Antwort (kein JSON-Objekt). Prüfe REACT_APP_SSO_BACKEND, Proxy und API-Logs.',
          status,
          rawPreview: typeof data === 'string' ? data.slice(0, 200) : String(data).slice(0, 200),
        });
      }
      if (data.error && !data.provider) {
        return rejectWithValue({
          message: data.message || data.error,
          status,
          data,
          requestId: data.requestId,
        });
      }
      if (data.provider !== 'sso') {
        return rejectWithValue({
          message: `Unerwarteter provider "${data.provider}" — SSO-Callback lieferte keine SSO-Session.`,
          status,
          data,
        });
      }
      if (!data.microsoftJWT) {
        return rejectWithValue({
          message: 'Antwort ohne microsoftJWT — Backend-Session unvollständig.',
          status,
          data,
        });
      }
      return data;
    } catch (err) {
      return rejectWithValue(serializeAxiosError(err));
    }
  }
);
